import { useMemo } from "react";
import fuzzysort from "fuzzysort";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useListGoogleFonts } from "@/react/components/SettingsApp/Site/Pages/Edit/PageSettings/CustomFonts/GoogleFonts/hooks/useListGoogleFonts";
import { FONT_WEIGHT_OPTIONS } from "@/react/components/shared/BuilderUI/DefaultBlocks/Content/PrimaryButtonBlock/Editor/constants";
import { useBuilderStore } from "@/react/components/shared/BuilderUI/Hooks/useBuilderStore";
import { FormTextSelect } from "@/react/components/shared/uikit/Form/FormTextSelect";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { Form } from "@circle-react-shared/uikit/Form";
import { FONT_FAMILY_OPTIONS } from "../constants";

interface FontOption {
  group: string;
  label: string;
  value: string;
  variants?: string[];
}

const INITIAL_FONT_COUNT = 250;

export const FontPicker = ({
  typographyToken,
}: {
  typographyToken: string;
}) => {
  const { watch, setValue } = useFormContext();
  const generateFormItemName = (name: string) =>
    `theme.${typographyToken}.theme-website-${typographyToken}-${name}`;

  const fontFamily = watch(generateFormItemName("font-family")) as string;

  const { data: googleFonts, isLoading: isLoadingGoogleFonts } =
    useListGoogleFonts();

  const registerGoogleFontWeight = useBuilderStore(
    state => state.registerGoogleFontWeight,
  );

  const transformedGoogleFonts = useMemo(
    (): FontOption[] =>
      googleFonts?.fonts.slice(0, INITIAL_FONT_COUNT).map(font => ({
        group: "google",
        label: font.family,
        value: font.family,
        variants: font.variants,
      })) || [],
    [googleFonts],
  );

  const allFontOptions = useMemo(
    (): FontOption[] => [...transformedGoogleFonts, ...FONT_FAMILY_OPTIONS],
    [transformedGoogleFonts],
  );

  const customFilterBy = (
    options: FontOption[] | readonly FontOption[],
    search: string,
  ) => {
    if (!search.trim()) {
      return [...options];
    }

    const allGoogleFonts =
      googleFonts?.fonts.map(font => ({
        group: "google",
        label: font.family,
        value: font.family,
        variants: font.variants,
      })) || [];

    const allSearchableFonts = [...allGoogleFonts, ...FONT_FAMILY_OPTIONS];

    const searchResults = fuzzysort.go(search, allSearchableFonts, {
      key: "label",
      limit: 100,
      threshold: -10000,
    });

    return searchResults.map(result => result.obj);
  };

  const groupByFontFamily = useMemo(
    () => (option: FontOption) => option.group,
    [],
  );

  const fontWeightOptions = useMemo(() => {
    const isWebFont = FONT_FAMILY_OPTIONS.some(
      font => font.value === fontFamily,
    );

    if (isWebFont) {
      return FONT_WEIGHT_OPTIONS;
    }

    const selectedGoogleFont = transformedGoogleFonts.find(
      font => font.value === fontFamily,
    );

    if (selectedGoogleFont) {
      return (selectedGoogleFont.variants || []).map(variant => ({
        label: variant,
        value: variant,
      }));
    }

    return FONT_WEIGHT_OPTIONS;
  }, [fontFamily, transformedGoogleFonts]);

  const handleFontFamilyChange = (selectedFont: string) => {
    const isWebFont = FONT_FAMILY_OPTIONS.some(
      font => font.value === selectedFont,
    );

    let firstWeight: string;

    if (isWebFont) {
      firstWeight = FONT_WEIGHT_OPTIONS[0].value;
    } else {
      const selectedGoogleFont = googleFonts?.fonts.find(
        font => font.family === selectedFont,
      );

      if (selectedGoogleFont && selectedGoogleFont.variants.length > 0) {
        firstWeight = selectedGoogleFont.variants[0];
      } else {
        firstWeight = FONT_WEIGHT_OPTIONS[0].value;
      }
    }

    setValue(generateFormItemName("font-weight"), firstWeight, {
      shouldDirty: true,
      shouldTouch: true,
    });

    if (!isWebFont) {
      const isGoogleFont = googleFonts?.fonts.some(
        font => font.family === selectedFont,
      );

      if (isGoogleFont) {
        registerGoogleFontWeight({
          family: selectedFont,
          weight: firstWeight,
        });
      }
    }
  };

  const handleFontWeightChange = (selectedFontWeight: string) => {
    if (fontFamily) {
      const isGoogleFont = googleFonts?.fonts.some(
        font => font.family === fontFamily,
      );

      if (isGoogleFont) {
        registerGoogleFontWeight({
          family: fontFamily,
          weight: selectedFontWeight,
        });
      }
    }
  };

  if (isLoadingGoogleFonts) {
    return <Loader />;
  }

  return (
    <div>
      <Form.Item
        isFirstChild
        hideBorder
        hideDescription
        name={generateFormItemName("font-family")}
        label={t("builder_ui.css_variables.typography.variables.font_family")}
      >
        <FormTextSelect
          name={generateFormItemName("font-family")}
          options={allFontOptions}
          groupBy={groupByFontFamily}
          filterBy={customFilterBy}
          onChange={handleFontFamilyChange}
        />
      </Form.Item>
      <Form.Item
        hideBorder
        hideDescription
        name={generateFormItemName("font-weight")}
        label={t("builder_ui.css_variables.typography.variables.font_weight")}
      >
        <FormTextSelect
          name={generateFormItemName("font-weight")}
          options={fontWeightOptions}
          onChange={handleFontWeightChange}
        />
      </Form.Item>
    </div>
  );
};
